import { Button, Col, Row, Space, Spin, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

dayjs.extend(utc);

import { CreateNoteModal } from '@/components/common/CreateNoteModal';
import { BrowseAssetsDrawer } from '@/components/layout/BrowseAssetsDrawer';
import {
  FreeTextFieldsCard,
  HealthIndicatorsCard,
  InputMethodCard,
  // KeyMilestonesCard, // BDP-156: hidden, "Coming in V2" placeholder
  PreviousWeeklyReportCard,
} from '@/components/pages/weekly_update_page';
import { useAuthContext } from '@/hooks/auth.store';
import { useWeeklyStatusAssignment } from '@/hooks/useWeeklyStatusAssignment';
import {
  useWeeklyUpdate,
  type AddWeeklyUpdateRequest,
  type PreviousWeeklyUpdateRecord,
} from '@/hooks/useWeeklyUpdate';
import { useWeeklyUpdateAssets } from '@/hooks/useWeeklyUpdateAssets';
import { useWeeklyUpdateData } from '@/hooks/useWeeklyUpdateData';
import { useMostRecentWeeklyUpdate } from '@/hooks/useMostRecentWeeklyUpdate';
import { getCurrentWeekMonday, isWeekWithinEditWindow } from '@/utils/weeklyUpdateUtils';

import '@/styles/project_details_styles/weeklyupdatepage.scss';

// ─────────────────────────────────────────────────────────────────────
// Types & Constants
// ─────────────────────────────────────────────────────────────────────

export interface WeeklyUpdatePageProps {
  mode?: 'create' | 'edit' | 'view';
  clientId: string;
  projectId: string;
  clientLabel?: string;
  projectName?: string;
  week?: string;
  previousWeeklyUpdate?: PreviousWeeklyUpdateRecord | null;
  onBack?: () => void;
  canEditHealth?: boolean;
}

interface HealthFormData {
  onSchedule?: string;
  onBudget?: string;
  csat?: string;
  teamHealth?: string;
  notes?: string;
}

interface FreeTextFieldsData {
  accomplishments?: string;
  planNextWeek?: string;
  risksBlockers?: string;
}

interface EditModeState {
  isReadOnly: boolean;
  readOnlyReason: string;
}

const EMPTY_HEALTH = {};
const EMPTY_FIELDS = {};

// ─────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────

export function WeeklyUpdatePage({
  mode = 'create',
  clientId,
  projectId,
  clientLabel = 'Client',
  projectName = 'Project',
  week,
  previousWeeklyUpdate: _previousWeeklyUpdate,
  onBack,
  canEditHealth = true,
}: Readonly<WeeklyUpdatePageProps>): React.ReactElement {
  const { accessToken } = useAuthContext();
  const assets = useWeeklyUpdateAssets();
  const { isSubmitting, submitUpdate } = useWeeklyUpdate(accessToken, clientId, projectId);
  const { assignment } = useWeeklyStatusAssignment(clientId, projectId, accessToken);

  const [noteModalOpen, setNoteModalOpen] = useState(false);

  const [healthData, setHealthData] = useState<HealthFormData>(() => {
    if (mode === 'edit' && _previousWeeklyUpdate) {
      return {
        onSchedule: _previousWeeklyUpdate.onSchedule?.status,
        onBudget: _previousWeeklyUpdate.onBudget?.status ?? undefined,
        csat: _previousWeeklyUpdate.csat?.status,
        teamHealth: _previousWeeklyUpdate.teamHealth?.status,
        // Top-level notes is the canonical source — the backend stores a single
        // shared notes string, not per-indicator notes.
        notes: _previousWeeklyUpdate.notes ?? _previousWeeklyUpdate.onSchedule?.notes,
      };
    }
    return EMPTY_HEALTH;
  });

  const [fieldsData, setFieldsData] = useState<FreeTextFieldsData>(() => {
    if (mode === 'edit' && _previousWeeklyUpdate) {
      return {
        accomplishments: _previousWeeklyUpdate.accomplishments,
        planNextWeek: _previousWeeklyUpdate.planNextWeek,
        risksBlockers: _previousWeeklyUpdate.riskBlockers,
      };
    }
    return EMPTY_FIELDS;
  });

  // When in edit mode without pre-loaded data (e.g. direct URL navigation),
  // fetch the update ourselves. The week comes from the URL.
  const shouldFetch = mode === 'edit' && !_previousWeeklyUpdate;
  const { data: fetchedUpdate, loading: loadingUpdate } = useWeeklyUpdateData(
    accessToken,
    clientId,
    projectId,
    shouldFetch ? (week ?? '') : '',
  );

  // Seed the form from fetched data when it arrives (direct-URL case).
  useEffect(() => {
    if (!fetchedUpdate || !shouldFetch) {
      return;
    }

    setHealthData({
      onSchedule: fetchedUpdate.onSchedule?.status,
      onBudget: fetchedUpdate.onBudget?.status ?? undefined,
      csat: fetchedUpdate.csat?.status,
      teamHealth: fetchedUpdate.teamHealth?.status,
      notes: fetchedUpdate.notes ?? fetchedUpdate.onSchedule?.notes,
    });
    setFieldsData({
      accomplishments: fetchedUpdate.accomplishments,
      planNextWeek: fetchedUpdate.planNextWeek,
      risksBlockers: fetchedUpdate.riskBlockers,
    });
  }, [fetchedUpdate, shouldFetch]);

  // ─────────────────────────────────────────────────────────────────────
  // Date & Week Calculations
  // ─────────────────────────────────────────────────────────────────────

  const currentWeekMonday = getCurrentWeekMonday();

  // In edit/view mode, show the week being viewed; in create mode, show current week
  const displayWeekMonday =
    (mode === 'edit' || mode === 'view') && week
      ? week
      : (assignment?.assigned_week ?? currentWeekMonday);
  const formattedDate = dayjs.utc(displayWeekMonday).format('MMMM D, YYYY');

  // Determine the week to use for fetching previous update
  const currentAssignedWeek = useMemo(
    () => assignment?.assigned_week ?? currentWeekMonday,
    [assignment?.assigned_week, currentWeekMonday],
  );

  // In create mode: fetch most recent available update (any previous week with data)
  // In edit/view mode: use the provided _previousWeeklyUpdate
  const { data: mostRecentUpdate } = useMostRecentWeeklyUpdate(
    mode === 'create' ? accessToken : null,
    clientId,
    projectId,
    mode === 'create' ? currentAssignedWeek : '',
  );

  const previousUpdateToDisplay = useMemo(() => {
    if (mode === 'create') {
      return mostRecentUpdate;
    }
    return _previousWeeklyUpdate;
  }, [mode, mostRecentUpdate, _previousWeeklyUpdate]);

  // Check if a week is still within the editable window (Monday through Tuesday of next week)
  const isWeekStillEditable = useMemo(() => isWeekWithinEditWindow(week ?? '', true), [week]);

  // ─────────────────────────────────────────────────────────────────────
  // Validation & Computed State
  // ─────────────────────────────────────────────────────────────────────

  const allIndicatorsSelected =
    !!healthData.onSchedule &&
    !!healthData.onBudget &&
    !!healthData.csat &&
    !!healthData.teamHealth;

  const allIndicatorsGreen =
    healthData.onSchedule === 'green' &&
    healthData.onBudget === 'green' &&
    healthData.csat === 'green' &&
    healthData.teamHealth === 'green';

  const notesField = healthData.notes ?? '';
  const notesValidation = allIndicatorsGreen || notesField.trim().length > 0;
  const isSaveEnabled = allIndicatorsSelected && notesValidation;

  // ─────────────────────────────────────────────────────────────────────
  // Event Handlers
  // ─────────────────────────────────────────────────────────────────────

  const buildApiPayload = useCallback(
    (weekOf: string): AddWeeklyUpdateRequest => ({
      week_of: weekOf,
      notes: healthData.notes || '',
      on_schedule: {
        status: healthData.onSchedule || 'pending',
        notes: healthData.notes || '',
      },
      on_budget: {
        status: healthData.onBudget || 'pending',
        notes: healthData.notes || '',
      },
      csat: {
        status: healthData.csat || 'pending',
        notes: healthData.notes || '',
      },
      team_health: {
        status: healthData.teamHealth || 'pending',
        notes: healthData.notes || '',
      },
      accomplishments: (fieldsData.accomplishments as string) || '',
      plan_next_week: (fieldsData.planNextWeek as string) || '',
      risk_blockers: (fieldsData.risksBlockers as string) || '',
    }),
    [healthData, fieldsData],
  );

  const handleSave = useCallback(async () => {
    const weekToSave =
      (mode === 'edit' || mode === 'view') && week
        ? week
        : (assignment?.assigned_week ?? currentWeekMonday);
    const payload = buildApiPayload(weekToSave);
    await submitUpdate(payload, mode, weekToSave, () => {
      onBack?.();
    });
  }, [buildApiPayload, assignment, currentWeekMonday, mode, week, submitUpdate, onBack]);

  // ─────────────────────────────────────────────────────────────────────
  // Mode & Authorization
  // ─────────────────────────────────────────────────────────────────────

  const isReadOnly = useMemo((): boolean => {
    if (!canEditHealth) {
      return true;
    }

    // In edit mode, check if outside edit window
    if (mode === 'edit' && !isWeekStillEditable) {
      return true;
    }

    return false;
  }, [mode, canEditHealth, isWeekStillEditable]);

  const editModeState = useMemo((): EditModeState => {
    let readOnlyReason = '';

    if (!canEditHealth) {
      readOnlyReason = 'You are not authorized to update this';
    } else if (mode === 'edit' && !isWeekStillEditable) {
      readOnlyReason =
        'Edit window closed (updates can only be edited until Tuesday of the following week)';
    }

    return {
      isReadOnly,
      readOnlyReason,
    };
  }, [isReadOnly, canEditHealth, mode, isWeekStillEditable]);

  const pageTitle =
    mode === 'create'
      ? 'New Weekly Update'
      : isReadOnly
        ? `View Weekly Update`
        : 'Edit Weekly Update';

  // ─────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────

  const getSaveButtonTooltip = (): string => {
    if (editModeState.isReadOnly) {
      return editModeState.readOnlyReason;
    }
    if (!allIndicatorsSelected) {
      return 'Set all four health indicators first.';
    }
    if (!allIndicatorsGreen && !notesField.trim().length) {
      return 'Add notes before saving (not required if all indicators are green).';
    }
    return '';
  };

  return (
    <>
      <Space direction="vertical" size={0} className="page-stack pf-page">
        {/* Header */}
        <div className="weekly-update-page__header">
          <div className="weekly-update-page__header-content">
            <Typography.Title level={3} type="secondary" className="weekly-update-page__title">
              {pageTitle}
            </Typography.Title>
            <Typography.Text type="secondary" className="weekly-update-page__subtitle">
              {clientLabel} · {projectName} · for week of {formattedDate}
            </Typography.Text>
          </div>

          <div className="weekly-update-page__header-actions">
            {loadingUpdate && <Spin size="small" />}
            <Button onClick={onBack}>Cancel</Button>
            <Tooltip title={getSaveButtonTooltip()}>
              <Button
                type="primary"
                onClick={handleSave}
                disabled={
                  !isSaveEnabled || isSubmitting || editModeState.isReadOnly || loadingUpdate
                }
                loading={isSubmitting}
              >
                Save weekly update
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* Previous Report Card - Show in all modes */}
        {(mode === 'create' || mode === 'edit' || mode === 'view') && (
          <div className="weekly-update-page__section">
            <PreviousWeeklyReportCard
              data={previousUpdateToDisplay ?? undefined}
              week={previousUpdateToDisplay?.weekOf}
              clientId={clientId}
              projectId={projectId}
            />
          </div>
        )}

        {/* Input Method Card - Shown for create and edit modes */}
        {(mode === 'create' || mode === 'edit') && (
          <div className="weekly-update-page__section">
            <InputMethodCard
              disabled={editModeState.isReadOnly}
              onSourceChange={(source) => {
                if (source === 'llm') {
                  setFieldsData((prev) => ({ ...prev, _source: 'llm' }));
                }
              }}
              onFieldsExtracted={(extracted) => {
                setFieldsData((prev) => ({
                  accomplishments: extracted.accomplishments || prev.accomplishments,
                  planNextWeek: extracted.planNextWeek || prev.planNextWeek,
                  risksBlockers: extracted.riskBlockers || prev.risksBlockers,
                }));

                setHealthData((prev) => ({
                  onSchedule: extracted.onScheduleStatus || prev.onSchedule,
                  onBudget: prev.onBudget,
                  csat: extracted.csatStatus || prev.csat,
                  teamHealth: extracted.teamHealthStatus || prev.teamHealth,
                  notes: extracted.notes || prev.notes,
                }));
              }}
              clientId={clientId}
              projectId={projectId}
            />
          </div>
        )}

        {/* Form Cards */}
        <div className="weekly-update-page__section">
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <HealthIndicatorsCard
                onSchedule={healthData.onSchedule}
                onBudget={healthData.onBudget}
                csat={healthData.csat}
                teamHealth={healthData.teamHealth}
                notes={healthData.notes}
                onHealthChange={setHealthData}
                disabled={editModeState.isReadOnly || loadingUpdate}
              />
            </Col>
            <Col xs={24} md={12}>
              <FreeTextFieldsCard
                accomplishments={fieldsData.accomplishments}
                planNextWeek={fieldsData.planNextWeek}
                risksBlockers={fieldsData.risksBlockers}
                onFieldsChange={setFieldsData}
                disabled={editModeState.isReadOnly || loadingUpdate}
              />
            </Col>
          </Row>
        </div>

        {/* Milestones — BDP-156: hidden, "Coming in V2" placeholder
        <div className="weekly-update-page__section">
          <KeyMilestonesCard />
        </div>
        */}
      </Space>

      {/* Asset Management Modals */}
      <BrowseAssetsDrawer
        open={assets.browseOpen}
        onClose={assets.closeBrowse}
        clientId={clientId}
        projectId={projectId}
        accessToken={assets.accessToken ?? ''}
        linkedCardIds={assets.linkedDocCardIds}
        onSelect={(cardId) => {
          assets.addCard(cardId);
          assets.closeBrowse();
        }}
      />

      <CreateNoteModal
        open={noteModalOpen}
        assetSessionId={null}
        accessToken={assets.accessToken}
        clientId={clientId}
        projectId={projectId}
        onClose={() => setNoteModalOpen(false)}
        onSaved={(cardId) => {
          assets.addCard(cardId);
          setNoteModalOpen(false);
        }}
      />
    </>
  );
}
